import * as React from 'react';
import { type DataType, type ObjectDataType, isOptionalType } from '@glueapp/graph-types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ShapeTypeSelectorEditor } from './ShapeValueEditor';
import { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';

function deriveOptionalProperties(properties: Record<string, DataType>): string[] {
    return Object.entries(properties)
        .filter(([, type]) => isOptionalType(type))
        .map(([name]) => name);
}

interface ShapeObjectEditorProps {
    value: ObjectDataType;
    onChange: (value: ObjectDataType) => void;
}

export const ShapeObjectEditor: React.FC<ShapeObjectEditorProps> = ({ value, onChange }) => {
    const [newPropertyName, setNewPropertyName] = useState<string>('');
    const inputRef = React.useRef<HTMLInputElement>(null);

    const changeProperty = (propertyName: string, newValue: DataType) => {
        const updatedProperties = { ...value.properties, [propertyName]: newValue };
        onChange({
            ...value,
            properties: updatedProperties,
            optionalProperties: deriveOptionalProperties(updatedProperties),
        });
    };

    const removeProperty = (propertyName: string) => {
        const updatedProperties = { ...value.properties };
        delete updatedProperties[propertyName];
        onChange({
            ...value,
            properties: updatedProperties,
            optionalProperties: deriveOptionalProperties(updatedProperties),
        });
    };

    const addProperty = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (newPropertyName !== '') {
            onChange({
                ...value,
                properties: {
                    ...value.properties,
                    [newPropertyName]: { kind: 'string' },
                },
            });
            setNewPropertyName('');
            inputRef.current?.focus();
        }
    };

    const propertyEntries = Object.entries(value.properties);

    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-foreground">
                Object {propertyEntries.length > 0 && <span className="font-normal text-muted-foreground">({propertyEntries.length})</span>}
            </label>

            {propertyEntries.length === 0 ? (
                <div className="rounded-md border-2 border-dashed border-border p-3">
                    <p className="text-xs text-muted-foreground mb-2 text-center">No properties defined</p>
                    <form onSubmit={addProperty} className="flex flex-row gap-2">
                        <Input
                            ref={inputRef}
                            placeholder="Property name"
                            value={newPropertyName}
                            onChange={e => setNewPropertyName(e.target.value)}
                            className="flex-1"
                        />
                        <Button disabled={newPropertyName === ''} type="submit" variant="outline" size="sm">
                            <Plus className="h-3 w-3 mr-1" />
                            Add
                        </Button>
                    </form>
                </div>
            ) : (
                <div className="border-l-2 border-primary/40 pl-3 space-y-3">
                    {propertyEntries.map(([propertyName, propertyType]) => (
                        <div key={propertyName}>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold text-foreground">{propertyName}</span>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => removeProperty(propertyName)}
                                >
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                            <ShapeTypeSelectorEditor value={propertyType} onChange={newValue => changeProperty(propertyName, newValue)} />
                        </div>
                    ))}

                    <form onSubmit={addProperty} className="flex flex-row gap-2 pt-1">
                        <Input
                            ref={inputRef}
                            placeholder="Property name"
                            value={newPropertyName}
                            onChange={e => setNewPropertyName(e.target.value)}
                            className="flex-1"
                        />
                        <Button disabled={newPropertyName === ''} type="submit" variant="outline" size="sm">
                            <Plus className="h-3 w-3 mr-1" />
                            Add
                        </Button>
                    </form>
                </div>
            )}
        </div>
    );
};
